import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Breadcrumb from '../components/Breadcrumb';
import {
  CreditCard,
  Wallet,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
  ExternalLink,
  ArrowRight,
  Shield
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myPayments, setMyPayments] = useState([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchOptions();
    if (token) fetchMyPayments();
  }, [token]);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/payments/membership-options`);
      setOptions(res.data);
    } catch (err) {
      console.error('Error fetching payment options:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPayments = async () => {
    try {
      const res = await axios.get(`${API}/payments/my-payments`, { headers });
      setMyPayments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStripeCheckout = async (membershipTypeId) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        membership_type_id: membershipTypeId
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
      setSubmitting(false);
    }
  };

  const handleUsdtSubmit = async () => {
    if (!txHash.trim() || !selectedPlan) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/payments/usdt-submit`, {
        membership_type_id: selectedPlan.id,
        tx_hash: txHash.trim()
      }, { headers });
      setSubmitted(true);
      setTxHash('');
      fetchMyPayments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColors = {
    pending: 'text-amber-400 bg-amber-500/10',
    completed: 'text-emerald-400 bg-emerald-500/10',
    rejected: 'text-red-400 bg-red-500/10',
    failed: 'text-red-400 bg-red-500/10'
  };

  const statusIcons = {
    pending: <Clock className="w-4 h-4" />,
    completed: <CheckCircle className="w-4 h-4" />,
    rejected: <XCircle className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <Shield className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Login Required</h2>
          <p className="text-zinc-400 mb-6">Please login to access the payment page.</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const plans = options?.membership_types || [];

  return (
    <div className="min-h-screen bg-zinc-800">
      <Breadcrumb items={[{ label: 'Payment', href: null }]} />

      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Choose Your Plan</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Upgrade your membership to unlock premium features
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Membership Plans */}
        {plans.length === 0 ? (
          <div className="text-center py-16">
            <Wallet className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">No paid plans available</h2>
            <p className="text-zinc-400">Check back later for membership options.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => { setSelectedPlan(plan); setPaymentMethod(null); setSubmitted(false); setError(''); }}
                  className={`relative bg-zinc-900 rounded-2xl border-2 p-6 cursor-pointer transition-all hover:border-emerald-500/50 ${
                    selectedPlan?.id === plan.id
                      ? 'border-emerald-500 shadow-lg shadow-emerald-500/10'
                      : 'border-zinc-800'
                  }`}
                >
                  <h3 className="text-xl font-bold text-white mb-2">{plan.title}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-emerald-400">{plan.price}</span>
                    <span className="text-zinc-400">{plan.currency || 'USD'}</span>
                    <span className="text-zinc-500 text-sm">/ {plan.duration_days} days</span>
                  </div>
                  {plan.description_bullets && (
                    <ul className="space-y-2 mb-4">
                      {plan.description_bullets.map((b, i) => (
                        <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {selectedPlan?.id === plan.id && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Payment Method Selection */}
            {selectedPlan && (
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl font-bold text-white mb-6 text-center">Select Payment Method</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {options?.stripe_enabled && (
                    <button
                      onClick={() => { setPaymentMethod('stripe'); setSubmitted(false); setError(''); }}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        paymentMethod === 'stripe'
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                      }`}
                    >
                      <CreditCard className={`w-8 h-8 mb-3 ${paymentMethod === 'stripe' ? 'text-blue-400' : 'text-zinc-400'}`} />
                      <h3 className="text-white font-semibold mb-1">Credit Card</h3>
                      <p className="text-zinc-500 text-sm">Pay securely with Visa, Mastercard</p>
                    </button>
                  )}
                  {options?.usdt_enabled && (
                    <button
                      onClick={() => { setPaymentMethod('usdt'); setSubmitted(false); setError(''); }}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        paymentMethod === 'usdt'
                          ? 'border-emerald-500 bg-emerald-500/5'
                          : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
                      }`}
                    >
                      <Wallet className={`w-8 h-8 mb-3 ${paymentMethod === 'usdt' ? 'text-emerald-400' : 'text-zinc-400'}`} />
                      <h3 className="text-white font-semibold mb-1">USDT Crypto</h3>
                      <p className="text-zinc-500 text-sm">Pay with USDT ({options?.usdt_network})</p>
                    </button>
                  )}
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-400 text-sm text-center">
                    {error}
                  </div>
                )}

                {/* Stripe Payment */}
                {paymentMethod === 'stripe' && (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center">
                    <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Pay {selectedPlan.price} {selectedPlan.currency || 'USD'}</h3>
                    <p className="text-zinc-400 text-sm mb-6">You'll be redirected to a secure checkout page</p>
                    <Button
                      onClick={() => handleStripeCheckout(selectedPlan.id)}
                      disabled={submitting}
                      className="bg-blue-600 hover:bg-blue-700 px-8 py-3"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><CreditCard className="w-4 h-4 mr-2" /> Pay with Card <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </div>
                )}

                {/* USDT Payment */}
                {paymentMethod === 'usdt' && !submitted && (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
                    <div className="text-center mb-6">
                      <Wallet className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-1">
                        Send {selectedPlan.price} USDT
                      </h3>
                      <p className="text-zinc-400 text-sm">Network: {options?.usdt_network}</p>
                    </div>

                    {options?.payment_instructions && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 mb-6 text-amber-300 text-sm">
                        {options.payment_instructions}
                      </div>
                    )}

                    {/* Wallet Address */}
                    <div className="mb-6">
                      <Label className="text-zinc-400 text-xs mb-2 block">Wallet Address</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-zinc-800 rounded-lg p-3 font-mono text-sm text-emerald-400 break-all">
                          {options?.usdt_wallet_address}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(options?.usdt_wallet_address)}
                          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-shrink-0"
                        >
                          {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* TX Hash Input */}
                    <div className="mb-6">
                      <Label className="text-zinc-400 text-xs mb-2 block">Transaction Hash</Label>
                      <Input
                        value={txHash}
                        onChange={(e) => setTxHash(e.target.value)}
                        placeholder="Paste your transaction hash here..."
                        className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                      />
                    </div>

                    <Button
                      onClick={handleUsdtSubmit}
                      disabled={submitting || !txHash.trim()}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 py-3"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        'Submit Payment'
                      )}
                    </Button>
                  </div>
                )}

                {/* USDT Submitted */}
                {paymentMethod === 'usdt' && submitted && (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Payment Submitted</h3>
                    <p className="text-zinc-400 mb-6">
                      Your payment is being reviewed. Your membership will be activated once verified by our team.
                    </p>
                    <Button onClick={() => { setSubmitted(false); setPaymentMethod(null); setSelectedPlan(null); }} variant="outline" className="border-zinc-700 text-zinc-300">
                      Back to Plans
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Payment History */}
        {myPayments.length > 0 && (
          <div className="max-w-3xl mx-auto mt-16">
            <h2 className="text-xl font-bold text-white mb-6">Payment History</h2>
            <div className="space-y-3">
              {myPayments.map((p) => (
                <div key={p.id} className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[p.status]}`}>
                      {p.payment_method === 'stripe' ? <CreditCard className="w-5 h-5" /> : <Wallet className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{p.amount} {p.currency}</p>
                      <p className="text-zinc-500 text-xs">
                        {p.payment_method === 'stripe' ? 'Credit Card' : 'USDT'} - {new Date(p.created_at).toLocaleDateString('en-US')}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusColors[p.status]}`}>
                    {statusIcons[p.status]}
                    {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
